import { useEffect, useMemo, useRef, useState } from 'react'
import type { ParticipantShareInput } from '@/features/participantShare/participant-share'
import { buildLuckyWheelItems } from '@/lib/buildLuckyWheelItems'
import { formatRouletteUserError } from '@/lib/formatRouletteUserError'
import {
  landingRouletteGetSpins,
  landingRouletteGetShareContext,
  landingRouletteShare,
  landingRouletteUnlockShare,
  landingRouletteSpin,
  type LandingSpinResult,
  type PublicRouletteDisplay,
  type LandingRouletteSpinsResponse,
  type LandingRouletteShareContext,
} from '@/lib/landing-roulette'
import type { RoulettePlayClient } from '@/lib/roulette-play-client'
import { normalizePublicRouletteDisplay } from '@/lib/normalizePublicRouletteDisplay'
import {
  campaignBrandPaletteToStyle,
  resolveCampaignBrandColor,
  resolveCampaignButtonTextColor,
} from '@/lib/campaignBrandPalette'
import { LandingCampaignShareSheet } from '@/components/LandingCampaignShareSheet'
import { RouletteWheelFace } from './RouletteWheelFace'
import { RouletteIcon } from './RouletteIcon'
import { RouletteResult } from './RouletteResult'
import {
  buildRoulettePalette,
  prizeLabel,
  rouletteButtonSurface,
} from './rouletteGeometry'
import { useRouletteMotion } from './useRouletteMotion'
import { useRouletteSound } from './useRouletteSound'
import './LandingLuckyWheel.css'

export type LandingLuckyWheelProps = {
  campaignId: number
  donorToken?: string
  /** Identidade estável quando o jogo não usa a sessão do doador. */
  sessionKey?: string
  /**
   * Sessão de jogo alternativa. Quando informada, a roleta não chama a API
   * autenticada do doador. O objeto precisa ser estável entre renderizações.
   */
  playClient?: RoulettePlayClient
  /** Already fetched by the page; no duplicate public request. */
  publicDisplay: PublicRouletteDisplay
  primaryColor?: string | null
  buttonColor?: string | null
  buttonTextColor?: string | null
  shareUrl?: string
  shareTitle?: string | null
  shareText?: string | null
  storyInput?: Omit<ParticipantShareInput, 'origin'>
  onWin?: (result: LandingSpinResult | null) => void
  onSpinningChange?: (spinning: boolean) => void
  onUseCoupon?: (result: LandingSpinResult) => void | Promise<void>
  isCouponCheckoutLoading?: boolean
  isPreview?: boolean
  onVisibilityChange?: (isVisible: boolean) => void
  onBalanceChange?: (balance: LandingRouletteSpinsResponse) => void
  onBuyClick?: () => void
  externalBalance?: LandingRouletteSpinsResponse | null
  externalBalanceError?: string | null
}

const previewBalance: LandingRouletteSpinsResponse = {
  available_spins: 99,
  shares_available: 0,
  paid_purchases: 0,
  shares_used: 0,
  spins_per_share: 1,
}

// A different campaign or session owns its own requests, animation and audio lifecycle.
export function LandingLuckyWheel(props: LandingLuckyWheelProps) {
  return (
    <LuckyWheelSession
      key={`${props.campaignId}:${props.donorToken ?? props.sessionKey ?? 'guest'}:${!!props.isPreview}`}
      {...props}
    />
  )
}

function LuckyWheelSession({
  campaignId,
  donorToken,
  playClient,
  publicDisplay,
  primaryColor,
  buttonColor,
  buttonTextColor,
  shareUrl = '',
  shareTitle,
  shareText,
  storyInput,
  onWin,
  onSpinningChange,
  onUseCoupon,
  isCouponCheckoutLoading = false,
  isPreview = false,
  onVisibilityChange,
  onBalanceChange,
  onBuyClick,
  externalBalance,
  externalBalanceError,
}: LandingLuckyWheelProps) {
  const palette = useMemo(
    () => buildRoulettePalette(resolveCampaignBrandColor(primaryColor, buttonColor)),
    [primaryColor, buttonColor]
  )
  const brandStyle = useMemo<Record<string, string>>(
    () => ({
      ...campaignBrandPaletteToStyle(
        palette,
        resolveCampaignButtonTextColor(buttonTextColor)
      ),
      '--roulette-action-background': rouletteButtonSurface(
        palette.primary,
        resolveCampaignButtonTextColor(buttonTextColor)
      ),
    }),
    [palette, buttonTextColor]
  )
  const normalizedDisplay = useMemo(
    () => normalizePublicRouletteDisplay(publicDisplay),
    [publicDisplay]
  )
  const [spinDisplay, setSpinDisplay] = useState<PublicRouletteDisplay | null>(null)
  const display = spinDisplay ?? normalizedDisplay
  const items = useMemo(() => buildLuckyWheelItems(display, 'position'), [display])
  const [loadingData, setLoadingData] = useState(
    !isPreview && (Boolean(donorToken) || Boolean(playClient)) && externalBalance === undefined
  )
  const [spinning, setSpinning] = useState(false)
  const [sharing, setSharing] = useState(false)
  const [isApplyingCoupon, setIsApplyingCoupon] = useState(false)
  const [erro, setErro] = useState<string | null>(
    !isPreview && !donorToken && !playClient
      ? 'Entre na campanha para girar a roleta.'
      : null
  )
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [resultado, setResultado] = useState<LandingSpinResult | null>(null)
  const [balance, setBalance] = useState<LandingRouletteSpinsResponse | null>(
    isPreview ? previewBalance : null
  )
  const [shareContext, setShareContext] = useState<LandingRouletteShareContext | null>(
    null
  )
  const [shareSheetOpen, setShareSheetOpen] = useState(false)
  const alive = useRef(true)
  const previousExternalBalance = useRef(externalBalance)
  const spinLock = useRef(false)
  const spinButtonRef = useRef<HTMLButtonElement>(null)
  const shareLock = useRef(false)
  const sound = useRouletteSound()
  const motion = useRouletteMotion(sound.tick)
  const client = useMemo<RoulettePlayClient | null>(() => {
    if (playClient) return playClient
    if (!donorToken) return null
    return {
      getSpins: (id) => landingRouletteGetSpins(donorToken, id),
      spin: (id) => landingRouletteSpin(donorToken, id),
      getShareContext: (id) => landingRouletteGetShareContext(donorToken, id),
      share: (id) => landingRouletteShare(donorToken, id),
      unlockShare: (id) => landingRouletteUnlockShare(donorToken, id),
    }
  }, [donorToken, playClient])
  const availableSpins = balance?.available_spins ?? 0
  const locked = !isPreview && balance?.roulette_unlocked === false
  const couponBusy = isApplyingCoupon || isCouponCheckoutLoading

  useEffect(() => {
    alive.current = true
    return () => {
      alive.current = false
    }
  }, [])
  useEffect(() => {
    onVisibilityChange?.(true)
  }, [onVisibilityChange])
  useEffect(() => {
    if (previousExternalBalance.current !== externalBalance) {
      previousExternalBalance.current = externalBalance
      return
    }
    if (balance && balance !== externalBalance && !isPreview) onBalanceChange?.(balance)
  }, [balance, externalBalance, isPreview, onBalanceChange])
  useEffect(() => {
    if (externalBalance === undefined) return
    setBalance(externalBalance)
    setLoadingData(externalBalance === null && !externalBalanceError)
  }, [externalBalance, externalBalanceError])
  useEffect(() => {
    if (isPreview || !client) return
    if (externalBalance !== undefined) return
    let cancelled = false
    void client
      .getSpins(campaignId)
      .then((data) => {
        if (!cancelled) setBalance(data)
      })
      .catch((error: unknown) => {
        if (!cancelled) setErro(formatRouletteUserError(error))
      })
      .finally(() => {
        if (!cancelled) setLoadingData(false)
      })
    return () => {
      cancelled = true
    }
  }, [campaignId, client, isPreview, externalBalance])

  const refreshBalance = () => {
    if (!client || isPreview) return
    void client
      .getSpins(campaignId)
      .then((fresh) => {
        if (alive.current) setBalance(fresh)
      })
      .catch(() => {
        /* Keep the confirmed local balance until the next refresh. */
      })
  }

  const handleShare = async () => {
    if (shareLock.current || spinLock.current || resultado) return
    if (isPreview) {
      setSuccessMessage('Modo Preview: Simulação de compartilhamento concluída!')
      setBalance((current) =>
        current ? { ...current, available_spins: current.available_spins + 1 } : current
      )
      return
    }
    if (!client) return
    shareLock.current = true
    setSharing(true)
    setErro(null)
    setSuccessMessage(null)
    try {
      const [freshBalance, context] = await Promise.all([
        client.getSpins(campaignId),
        client.getShareContext(campaignId),
      ])
      if (!alive.current) return
      setBalance(freshBalance)
      setShareContext(context)
      setShareSheetOpen(true)
    } catch {
      if (alive.current)
        setErro('Não foi possível abrir o compartilhamento da campanha. Tente novamente.')
    } finally {
      shareLock.current = false
      if (alive.current) setSharing(false)
    }
  }

  const girar = async () => {
    if (
      spinLock.current ||
      loadingData ||
      sharing ||
      shareSheetOpen ||
      resultado ||
      items.length === 0
    )
      return
    if (locked) {
      void handleShare()
      return
    }
    if (availableSpins < 1) {
      if (onBuyClick) onBuyClick()
      else setErro('Você não tem giros disponíveis no momento.')
      return
    }
    spinLock.current = true
    setSpinning(true)
    onSpinningChange?.(true)
    setSpinDisplay(normalizedDisplay)
    setErro(null)
    setSuccessMessage(null)
    sound.unlock()
    motion.start(items.length)
    try {
      let result: LandingSpinResult
      if (isPreview) {
        const position = Math.floor(Math.random() * normalizedDisplay.positions) + 1
        const prize = normalizedDisplay.prizes.find((p) => p.position === position)
        result = {
          position,
          prize_type: prize?.prize_type ?? 'none',
          prize_payload: prize?.display ?? null,
        }
      } else {
        if (!client) throw new Error('Entre na campanha para girar a roleta.')
        result = await client.spin(campaignId)
      }
      if (!alive.current) return
      const completed = await motion.land(result.position)
      if (!completed || !alive.current) return
      setResultado(result)
      setBalance((current) =>
        current
          ? { ...current, available_spins: Math.max(0, current.available_spins - 1) }
          : current
      )
      sound.finish(result.prize_type !== 'none')
      refreshBalance()
      onWin?.(result)
    } catch (error) {
      motion.cancel()
      if (alive.current) {
        setErro(formatRouletteUserError(error))
        setSpinDisplay(null)
      }
    } finally {
      spinLock.current = false
      if (alive.current) {
        setSpinning(false)
        onSpinningChange?.(false)
      }
    }
  }

  const handleCloseResult = () => {
    if (couponBusy) return
    setResultado(null)
    setSpinDisplay(null)
    setErro(null)
  }
  const showUseCoupon =
    !isPreview &&
    resultado?.prize_type === 'coupon' &&
    typeof resultado.prize_payload?.code === 'string' &&
    !!onUseCoupon
  const handleUseCoupon = async () => {
    if (!resultado || !showUseCoupon || couponBusy) return
    setIsApplyingCoupon(true)
    setErro(null)
    try {
      await onUseCoupon?.(resultado)
      if (alive.current) {
        setResultado(null)
        setSpinDisplay(null)
      }
    } catch (error) {
      if (alive.current)
        setErro(
          error instanceof Error
            ? error.message
            : 'Não foi possível abrir a compra com cupom. Tente novamente.'
        )
    } finally {
      if (alive.current) setIsApplyingCoupon(false)
    }
  }

  const handleConfirmShare = async () => {
    if (!client) throw new Error('Entre na campanha para compartilhar.')
    setSharing(true)
    try {
      if (
        (balance?.spins_per_share === 0 && balance.paid_purchases > 0) ||
        (balance?.achievement_spins_earned && balance.shares_available === 0)
      ) {
        const unlock = await client.unlockShare(campaignId)
        if (alive.current) {
          setBalance((current) =>
            current
              ? { ...current, roulette_unlocked: unlock.roulette_unlocked }
              : current
          )
          setSuccessMessage('Compartilhamento registrado. Sua roleta está liberada!')
        }
        return {
          spinsAdded: 0,
          alreadyShared: false,
          rouletteUnlocked: unlock.roulette_unlocked,
        }
      }
      const result = await client.share(campaignId)
      if (alive.current) {
        if (result.spins_added > 0) {
          setBalance((current) =>
            current
              ? {
                  ...current,
                  available_spins: current.available_spins + result.spins_added,
                  shares_available: result.shares_available,
                  shares_used: current.shares_used + 1,
                  roulette_unlocked: true,
                }
              : current
          )
          setSuccessMessage(
            `Parabéns! Você ganhou ${result.spins_added} ${result.spins_added === 1 ? 'giro' : 'giros'}.`
          )
        } else if (result.already_shared) {
          setBalance((current) =>
            current ? { ...current, shares_available: 0 } : current
          )
        }
        refreshBalance()
      }
      return { spinsAdded: result.spins_added, alreadyShared: result.already_shared }
    } finally {
      if (alive.current) setSharing(false)
    }
  }

  const sharePayload = {
    shareUrl: shareContext?.share_url || shareUrl.trim() || window.location.href,
    shareTitle: shareContext?.share_title || shareTitle?.trim() || 'Campanha',
    shareDescription:
      shareContext?.share_description ||
      shareText?.trim() ||
      'Participe desta campanha comigo e tente a sorte na roleta.',
  }
  const centerLabel = loadingData
    ? 'Carregando…'
    : spinning
      ? 'Girando…'
      : locked
        ? 'LIBERAR'
        : availableSpins > 0
          ? 'GIRAR'
          : onBuyClick
            ? 'COMPRAR AGORA'
            : 'SEM GIROS'
  const status = loadingData
    ? 'Preparando sua roleta…'
    : spinning
      ? 'Vamos descobrir o seu prêmio…'
      : locked
        ? 'Compartilhe a campanha para liberar sua roleta.'
        : availableSpins > 0
          ? 'Seu próximo prêmio pode estar a um giro.'
          : 'Participe da campanha para ganhar mais giros.'

  return (
    <div className="landingLuckyWheelRoot" style={brandStyle}>
      <div className="roulette-heading">
        <div>
          <p className="roulette-eyebrow">SUA CHANCE DE GANHAR</p>
          <h2>Roleta da sorte</h2>
        </div>
        <button
          type="button"
          className="roulette-sound"
          onClick={sound.toggle}
          aria-label={sound.enabled ? 'Desativar som da roleta' : 'Ativar som da roleta'}
          aria-pressed={sound.enabled}
          title={sound.enabled ? 'Som ativado' : 'Som desativado'}
        >
          <RouletteIcon kind={sound.enabled ? 'sound' : 'muted'} />
        </button>
      </div>
      <div className="roulette-balance" aria-label="Seu saldo da roleta">
        <div className="roulette-balance-primary">
          <strong>{loadingData ? '—' : availableSpins}</strong>
          <span>{isPreview ? 'giros de teste' : 'giros disponíveis'}</span>
        </div>
        {!isPreview && (
          <>
            <div>
              <strong>{balance?.shares_available ?? '—'}</strong>
              <span>
                bônus por
                <br />
                compartilhar
              </span>
            </div>
            <div>
              <strong>{balance?.paid_purchases ?? '—'}</strong>
              <span>
                compras
                <br />
                pagas
              </span>
            </div>
          </>
        )}
      </div>
      <div
        className={`roulette-stage${spinning ? ' roulette-stage--spinning' : ''}${resultado && resultado.prize_type !== 'none' ? ' roulette-stage--won' : ''}`}
      >
        <div className="roulette-wheel">
          <RouletteWheelFace
            items={items}
            palette={palette}
            rotorRef={motion.rotorRef}
            pointerRef={motion.pointerRef}
            selectedPosition={resultado?.position}
            spinning={spinning}
          />
          <button
            ref={spinButtonRef}
            type="button"
            className="roulette-hub"
            onClick={() => void girar()}
            disabled={
              spinning ||
              loadingData ||
              sharing ||
              shareSheetOpen ||
              !!resultado ||
              (!availableSpins && !locked && !onBuyClick)
            }
            aria-label={centerLabel}
          >
            {(loadingData || spinning || locked || availableSpins > 0) && (
              <span className="roulette-hub-symbol" aria-hidden="true">
                {spinning || loadingData ? (
                  <span className="roulette-hub-loader" />
                ) : (
                  <RouletteIcon
                    kind={locked ? 'share' : availableSpins ? 'arrow' : 'gift'}
                  />
                )}
              </span>
            )}
            <span>
              {loadingData ? (
                '…'
              ) : spinning ? (
                'GIRANDO'
              ) : centerLabel === 'COMPRAR AGORA' ? (
                <>
                  COMPRAR
                  <br />
                  AGORA
                </>
              ) : (
                centerLabel
              )}
            </span>
          </button>
        </div>
      </div>
      <p className="roulette-status" role="status" aria-live="polite">
        <span
          className={
            spinning
              ? 'roulette-status-dot roulette-status-dot--busy'
              : 'roulette-status-dot'
          }
        />
        {status}
      </p>
      {erro && !resultado && (
        <div className="roulette-notice roulette-notice--error" role="alert">
          {erro}
        </div>
      )}
      {successMessage && (
        <div className="roulette-notice" role="status">
          {successMessage}
        </div>
      )}
      <div className="roulette-share-area">
        <button
          type="button"
          className="roulette-share"
          onClick={() => void handleShare()}
          disabled={
            sharing ||
            loadingData ||
            spinning ||
            shareSheetOpen ||
            !!resultado ||
            (!isPreview && !client)
          }
        >
          <RouletteIcon kind="share" />
          <span>
            {isPreview
              ? 'Testar Compartilhar'
              : sharing
                ? 'Preparando…'
                : 'Compartilhar campanha'}
          </span>
          <RouletteIcon kind="arrow" />
        </button>
        {!isPreview && balance && (
          <p className="wheel-balance-note">
            {balance.spins_per_share > 0
              ? `${balance.shares_used} de ${balance.paid_purchases} oportunidades usadas · ${balance.spins_per_share} ${balance.spins_per_share === 1 ? 'giro' : 'giros'} por compartilhamento confirmado.`
              : 'Esta campanha não oferece giros por compartilhamento.'}
          </p>
        )}
      </div>
      <details className="roulette-prizes">
        <summary>
          Veja os prêmios desta roleta<span aria-hidden="true">+</span>
        </summary>
        <ul>
          {items.map((item, index) => {
            const label = prizeLabel(item)
            return (
              <li key={item.hash}>
                <span className="roulette-prize-position">
                  {String(index + 1).padStart(2, '0')}
                </span>
                <RouletteIcon kind={item.premio?.tipo ?? 'none'} />
                <span>
                  <strong>{label.value}</strong>
                  {label.type && <small>{label.type}</small>}
                </span>
              </li>
            )
          })}
        </ul>
      </details>
      {isPreview && <p className="roulette-preview-note">Modo Visualização (Teste)</p>}
      {resultado && (
        <RouletteResult
          result={resultado}
          brandStyle={brandStyle}
          preview={isPreview}
          busy={couponBusy}
          error={erro}
          returnFocusRef={spinButtonRef}
          onClose={handleCloseResult}
          onUseCoupon={showUseCoupon ? () => void handleUseCoupon() : undefined}
        />
      )}
      {shareSheetOpen && (
        <LandingCampaignShareSheet
          isOpen
          payload={sharePayload}
          balance={balance}
          storyInput={storyInput}
          brandStyle={brandStyle}
          onClose={() => setShareSheetOpen(false)}
          onConfirmShare={handleConfirmShare}
        />
      )}
    </div>
  )
}
